import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProducts, getResearchPortfolio } from '../api/api';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import { VERTICAL_PACKS, CLINICAL_DISCLAIMER } from '../data/ewm';
import './Products.css';
import './Narrative.css';

function Products() {
  const [products, setProducts] = useState([]);
  const [research, setResearch] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [productsRes, researchRes] = await Promise.all([
          getProducts(),
          getResearchPortfolio()
        ]);
        setProducts(productsRes.data || []);
        setResearch(researchRes.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  return (
    <div className="products-page">
      <SEO {...getSEOData('products')} />
      {/* Header */}
      <section className="page-header">
        <div className="container">
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">
            NanoLM, the AI Enterprise Suite, and vertical packs built on the same structural memory
            layer — self-hosted, CPU-first, and deployable inside your own boundary
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="section">
        <div className="container">
          <div className="products-grid">
            {products.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-logo">{product.logo}</div>
                <h2 className="product-name">{product.name}</h2>
                <p className="product-description">{product.description}</p>

                {product.name === 'NanoLM' && (
                  <div className="np-disclaimer" style={{ marginBottom: '16px' }}>
                    {CLINICAL_DISCLAIMER}
                  </div>
                )}

                {product.name === 'AI-PMS' && (
                  <div className="product-screenshot">
                    <img
                      src="/pms.png"
                      alt="AI-PMS — AI-Powered Project Management platform"
                      style={{
                        width: '70%',
                        display: 'block',
                        margin: '0 auto 8px',
                        borderRadius: '10px',
                        border: '1px solid rgba(25,118,210,0.15)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
                      }}
                    />
                    <p style={{ fontSize: '0.78rem', color: '#888', textAlign: 'center', margin: '0 0 16px' }}>
                      AI-PMS — AI-Powered Project Management platform
                    </p>
                  </div>
                )}

                {product.name === 'SGS.ai' && (
                  <div className="product-screenshot">
                    <img
                      src="/sgsai.png"
                      alt="SGS.ai Sovereign Governance Suite — metadata management, semantic search & governance dashboards"
                      style={{
                        width: '70%',
                        display: 'block',
                        margin: '0 auto 8px',
                        borderRadius: '10px',
                        border: '1px solid rgba(25,118,210,0.15)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
                      }}
                    />
                    <p style={{ fontSize: '0.78rem', color: '#888', textAlign: 'center', margin: '0 0 16px' }}>
                      SGS.ai Sovereign Governance Suite — metadata management, semantic search & governance dashboards
                    </p>
                  </div>
                )}

                <div className="product-features">
                  <h3>Key Features:</h3>
                  <ul>
                    {product.features && product.features.map((feature, index) => (
                      <li key={index}>
                        <span className="feature-dot">●</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {product.name === 'NanoLM' ? (
                  <Link to="/nanolm" className="btn btn-primary btn-full">
                    Open NanoLM →
                  </Link>
                ) : product.name === 'GoDriver' ? (
                  <button 
                    className="btn btn-secondary btn-full" 
                    disabled
                    style={{ cursor: 'not-allowed', opacity: 0.7 }}
                  >
                    Under Development
                  </button>
                ) : ['AI-PMS', 'SGS.ai', 'Inventory Management System', 'LIS — Laboratory Information System'].includes(product.name) ? (
                  <a 
                    href="/contact" 
                    className="btn btn-primary btn-full"
                  >
                    Request a Demo →
                  </a>
                ) : (
                  <a 
                    href={product.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn btn-primary btn-full"
                  >
                    Visit {product.name} →
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Research & Innovation Portfolio */}
          {research.length > 0 && (
            <div className="research-portfolio" style={{ marginTop: '80px' }}>
              <h2 style={{ fontSize: '2.2rem', fontWeight: 800, textAlign: 'center', marginBottom: '12px' }}>
                AI Research & Innovation — Products in Progress
              </h2>
              <p style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto 40px', color: '#555' }}>
                Five products we are actively prototyping and shipping — language models, world models,
                vision, document intelligence and vision-language-action autonomy.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '20px'
              }}>
                {research.map((item) => (
                  <div key={item.id} style={{
                    background: 'var(--white, #fff)',
                    borderRadius: '14px',
                    padding: '22px 22px 18px',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(25, 118, 210, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '1.8rem' }}>{item.icon}</span>
                      <span style={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        background: item.status === 'Implemented' ? '#E8F5E9' :
                                    item.status === 'Near-term' ? '#FFF3E0' :
                                    item.status === 'Medium-term' ? '#E3F2FD' : '#F3E5F5',
                        color: item.status === 'Implemented' ? '#2E7D32' :
                               item.status === 'Near-term' ? '#E65100' :
                               item.status === 'Medium-term' ? '#1565C0' : '#6A1B9A'
                      }}>
                        {item.status}
                      </span>
                    </div>
                    <h3 style={{ margin: '4px 0 0', fontSize: '1.15rem', fontWeight: 800 }}>{item.name}</h3>
                    <div style={{ fontSize: '0.78rem', color: '#666', fontWeight: 600 }}>{item.category}</div>
                    <p style={{ margin: '6px 0 0', fontSize: '0.92rem', color: '#444', lineHeight: 1.5 }}>
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vertical packs built on the side-car */}
          <div className="research-portfolio" style={{ marginTop: '80px' }}>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 800, textAlign: 'center', marginBottom: '12px' }}>
              Vertical Packs
            </h2>
            <p style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto 40px', color: '#555' }}>
              Packaged, domain-tuned applications built on the same measurement layer — a shared
              measurement substratum, one per buyer type.
            </p>
            <div className="np-grid np-grid--3">
              {VERTICAL_PACKS.map((pack) => (
                <div key={pack.id} className="np-card np-card--highlight">
                  <div className="np-card-icon">{pack.icon}</div>
                  <h3 className="np-h3">{pack.name}</h3>
                  <p className="np-body">{pack.body}</p>
                  <span className="np-badge np-badge--muted">{pack.status}</span>
                </div>
              ))}
            </div>
            <div className="np-cta-row np-cta-row--center">
              <Link to="/applications" className="np-btn np-btn--secondary">Applications &amp; industries</Link>
            </div>
          </div>

          {/* Additional Info */}
          <div className="products-info">
            <div className="info-box">
              <h2>Why Our Products?</h2>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-icon">🎯</div>
                  <h3>User-Centric Design</h3>
                  <p>Built with the end-user in mind for maximum usability and satisfaction.</p>
                </div>
                <div className="info-item">
                  <div className="info-icon">🔒</div>
                  <h3>Self-Hosted &amp; Auditable</h3>
                  <p>Content-addressed storage, immutable commit history and an append-only audit trail — with no outbound calls.</p>
                </div>
                <div className="info-item">
                  <div className="info-icon">⚡</div>
                  <h3>Fast & Efficient</h3>
                  <p>Optimized performance for speed and efficiency.</p>
                </div>
                <div className="info-item">
                  <div className="info-icon">💡</div>
                  <h3>Innovative Solutions</h3>
                  <p>Cutting-edge technology to solve real-world problems.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="products-cta">
        <div className="container">
          <div className="cta-box">
            <h2>Ready to Modernize Your Enterprise?</h2>
            <p>Book a personalized walkthrough of the AI Enterprise Suite — or talk to us about a custom AI build.</p>
            <a href="/contact" className="btn btn-large">Request a Demo</a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Products;
